import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { Stack, ControlledInput, ControlledCheckbox, Typography } from '@ui-components/neo.tax';
import { useEffect, useState } from 'react';
import { Control, FormState, UseFormHandleSubmit, UseFormWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useAuthErrorMessage, useDomainRedirect } from '@neo.tax/onboarding/hooks';
import {
  DomainRedirectInterstitial,
  StyledGeneralFormError,
  StyledLinkText,
  SubmitButton,
} from '@neo.tax/onboarding/organisms';
import {
  AuthenticationState,
  Path,
  getPath,
  signInWithEmailAndPassword,
  useAnalyticId,
  useUrlQueryParam,
  validateEmailAddress,
  validateFullName,
  validatePassword,
} from '@neo.tax/shared';
import { PROVISION_EMAIL_PASSWORD_USER } from '@neo.tax/shared/graphql/documents.graphql';
import {
  ProvisionEmailPasswordUserMutation,
  ProvisionEmailPasswordUserMutationVariables,
} from '@neo.tax/shared/graphql/types';
import { readProvisioningErrorReason } from '@neo.tax/shared/third-parties';

import { SignupFormValues } from './signup-form.types';

const INVALID_PASSWORD_ERROR = 'Invalid username and/or password.';

export const DefaultSignupForm = ({
  control,
  formState,
  handleSubmit,
  watch,
}: {
  control: Control<SignupFormValues, any>;
  handleSubmit: UseFormHandleSubmit<SignupFormValues>;
  formState: FormState<SignupFormValues>;
  watch: UseFormWatch<SignupFormValues>;
}): React.ReactElement => {
  const emailInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_EMAIL_INPUT);
  const fullNameInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_FULL_NAME_INPUT);
  const passwordInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PASSWORD_INPUT);
  const confirmPasswordInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PASSWORD_CONFIRMATION_INPUT);
  const signUpUserId = useAnalyticId(AnalyticIdentifier.ONBOARDING_SIGN_UP_USER);
  const tosAcceptedId = useAnalyticId(AnalyticIdentifier.TOS_ACCEPTED_CHECKBOX);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useAuthErrorMessage();
  const { isRedirecting, redirectIfSingleTenant } = useDomainRedirect();
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  const [password, confirmPassword] = watch(['password', 'confirmPassword']);

  const [provisionEmailPasswordUser] = useMutation<
    ProvisionEmailPasswordUserMutation,
    ProvisionEmailPasswordUserMutationVariables
  >(PROVISION_EMAIL_PASSWORD_USER);
  const navigate = useNavigate();
  // Token from /signup?inviteToken=XYZ. Invited users skip email verification entirely: after
  // signup we route them straight to /accept-invitation, and accepting the invite verifies
  // their email server-side (the one-time token proves they control the invited inbox).
  const inviteToken = useUrlQueryParam('inviteToken');

  useEffect(() => {
    if (password !== confirmPassword) {
      setConfirmPasswordError("Password doesn't match.");
    } else {
      setConfirmPasswordError(null);
    }
  }, [password, confirmPassword]);

  const onSubmit = async ({ email, fullName, password }: SignupFormValues): Promise<void> => {
    setIsSubmitted(true);
    const trimmedEmail = email.trim().toLowerCase();
    // Redirect single-tenant-domain users to their instance instead of creating an account here.
    if (await redirectIfSingleTenant(trimmedEmail, { forwardEmail: true })) {
      return;
    }
    // If we arrived from an invitation link, the post-signup destination is /accept-invitation:
    // accepting the invite verifies the email server-side, so invited users bypass the
    // /verify-email-sent step. We still pass this as `continueUrl` to provisionEmailPasswordUser so the
    // best-effort verification email also points back to acceptance as a fallback.
    const acceptInvitationUrl = inviteToken ? `/accept-invitation?token=${encodeURIComponent(inviteToken)}` : null;
    try {
      await provisionEmailPasswordUser({
        variables: {
          email: trimmedEmail,
          fullName,
          password,
          continueUrl: acceptInvitationUrl ?? undefined,
        },
      });
      // Sign the user in so they hold a Firebase ID token. For non-invited signups this is a
      // limbo state (authenticated but email_verified: false) and the resend button on
      // /verify-email-sent uses this token; invited users instead accept next, which verifies.
      const failure = await signInWithEmailAndPassword(trimmedEmail, password);
      if (failure) {
        setErrorMessage(failure.message);
        setIsSubmitted(false);
        return;
      }
      // Invited users go straight to /accept-invitation (acceptance verifies their email).
      // Everyone else lands on /verify-email-sent — the auth guard rejects authenticated calls
      // until the email is verified, so we can't drop them on the dashboard.
      navigate(acceptInvitationUrl ?? getPath({ path: Path.VERIFY_EMAIL_SENT }));
    } catch (error) {
      // Provisioning can refuse rather than fail: an organization has invited this address and admits only
      // its own SSO provider. Nothing about the password is wrong, so the credential copy below would send
      // them to change one — routed through the shared reason instead, which words it the same here as
      // everywhere else the refusal can surface.
      const reason = readProvisioningErrorReason(error);
      if (reason) {
        AuthenticationState.updateAuthErrorType(reason);
      } else {
        setErrorMessage(INVALID_PASSWORD_ERROR);
      }
      setIsSubmitted(false);
    }
  };

  if (isRedirecting) {
    return <DomainRedirectInterstitial />;
  }

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
      <ControlledInput
        control={control}
        name="email"
        rules={{ required: true, validate: validateEmailAddress }}
        id={emailInputId}
        label="Work Email Address"
        placeholder="Work Email Address"
      />
      <ControlledInput
        control={control}
        name="fullName"
        rules={{ required: true, validate: validateFullName }}
        id={fullNameInputId}
        label="Full Name"
        placeholder="Full Name"
      />
      <ControlledInput
        control={control}
        name="password"
        rules={{ validate: validatePassword }}
        id={passwordInputId}
        label="Password"
        type="password"
        placeholder="Password"
      />
      <ControlledInput
        control={control}
        name="confirmPassword"
        rules={{ required: true }}
        id={confirmPasswordInputId}
        label="Confirm Password"
        type="password"
        errorText={confirmPasswordError || undefined}
        placeholder="Confirm Password"
      />
      <Stack direction="column" gap={5}>
        <ControlledCheckbox
          control={control}
          name="tosAccepted"
          rules={{ required: true }}
          label={
            <Typography as="span" fontSize="sm" fontWeight="medium">
              I agree to Neo.Tax&apos;s{' '}
              <StyledLinkText
                as="a"
                fontSize="sm"
                fontWeight="medium"
                target="_blank"
                rel="noreferrer"
                href="//www.neo.tax/legal/privacy-policy"
              >
                privacy policy
              </StyledLinkText>{' '}
              and{' '}
              <StyledLinkText
                as="a"
                fontSize="sm"
                fontWeight="medium"
                target="_blank"
                rel="noreferrer"
                href="//www.neo.tax/legal/terms-of-service"
              >
                terms of service
              </StyledLinkText>
            </Typography>
          }
          id={tosAcceptedId}
        />
        <SubmitButton
          disabled={!!confirmPasswordError || !formState.isValid || isSubmitted}
          variant="primary"
          rounded="regular"
          size="base"
          type="submit"
          id={signUpUserId}
        >
          Sign Up
        </SubmitButton>
      </Stack>
      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
